import {
  ref,
  watch,
  onBeforeUnmount,
  onScopeDispose,
  getCurrentScope,
  getCurrentInstance,
  toRef,
  computed,
  toValue
} from 'vue';
import { isNil } from 'lodash';
import type { MaybeRefOrGetter, Ref, ComputedRef } from 'vue';

type Serializer<T> = (value: T) => string;
type Deserializer<T> = (value: string) => T;

interface StorageOptions<T> {
  userId?: MaybeRefOrGetter<string | undefined>;
  serializer?: Serializer<T>;
  deserializer?: Deserializer<T>;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function defaultSerialize<T>(value: T): string {
  return JSON.stringify(value);
}

function defaultDeserialize<T>(value: string): T {
  return JSON.parse(value) as T;
}

function makeScopedKey(key: string, userId?: string): string {
  if (!userId) {
    return key;
  }

  return `${key}_${userId}`;
}

function resolveStorageKey<T>(key: string, options?: StorageOptions<T>): string | undefined {
  const hasUserScope = Boolean(options && 'userId' in options);
  const userId = toValue(options?.userId);

  if (!hasUserScope) {
    return key;
  }

  if (!userId) {
    return undefined;
  }

  return makeScopedKey(key, userId);
}

export function getLocalStorageValue<T>(
  key: string,
  fallback?: T,
  options?: StorageOptions<T>
): T | undefined {
  if (!isBrowser()) {
    return fallback;
  }

  try {
    const storageKey = resolveStorageKey(key, options);
    if (!storageKey) {
      return fallback;
    }

    const raw = window.localStorage.getItem(storageKey);

    if (isNil(raw)) {
      return fallback;
    }

    const deserializer = options?.deserializer ?? defaultDeserialize<T>;
    return deserializer(raw);
  } catch {
    return fallback;
  }
}

export function setLocalStorageValue<T>(key: string, value: T, options?: StorageOptions<T>): void {
  if (!isBrowser()) {
    return;
  }

  try {
    const storageKey = resolveStorageKey(key, options);
    if (!storageKey) {
      return;
    }

    const serializer = options?.serializer ?? defaultSerialize<T>;
    window.localStorage.setItem(storageKey, serializer(value));
  } catch {
    // no-op
  }
}

export function removeLocalStorageValue<T>(key: string, options?: StorageOptions<T>): void {
  if (!isBrowser()) {
    return;
  }

  try {
    const storageKey = resolveStorageKey(key, options);
    if (!storageKey) {
      return;
    }

    window.localStorage.removeItem(storageKey);
  } catch {
    // no-op
  }
}

/**
 * Creates a reactive localStorage-backed ref.
 *
 * Scoping behavior:
 * - When `options.userId` is not provided, persistence is unscoped and reads/writes `key`.
 * - When `options.userId` is provided, persistence is scoped per user (`${key}_${userId}`).
 * - In scoped mode, if `userId` is currently unresolved (`undefined`), localStorage I/O is deferred
 *   and state remains in-memory until `userId` becomes available. This prevents transient writes to
 *   the unscoped/global key during auth initialization.
 *
 * @example
 * const { ref: localeRef } = useLocalStorageRef('locale', 'en', { userId: computed(() => auth.user?.id) });
 *
 * In this example, `localeRef` will be persisted to `localStorage` under a key specific to the authenticated user (e.g., `locale_123`). If no user is authenticated, it will not read/write to localStorage, and will simply use the default value `'en'`. Once a user logs in and `userId` becomes available, it will sync with the corresponding localStorage key for that user.
 */
export function useLocalStorageRef<T>(
  key: string | Ref<string> | ComputedRef<string>,
  defaultValue: T,
  options?: StorageOptions<T>
): { ref: Ref<T>; set: (value: T) => void; get: () => T; dispose: () => void } {
  const keyRef = toRef(key);
  const hasUserScope = Boolean(options && 'userId' in options);
  const userIdRef = computed(() => toValue(options?.userId));
  const isUserScopeReady = computed(() => !hasUserScope || Boolean(userIdRef.value));
  const resolvedStorageKey = computed(() => resolveStorageKey(keyRef.value, options));

  const readCurrentScopedValue = () => {
    if (!isUserScopeReady.value) {
      return defaultValue;
    }

    return getLocalStorageValue<T>(keyRef.value, defaultValue, options) ?? defaultValue;
  };

  const initial = readCurrentScopedValue();
  const state = ref<T>(initial) as Ref<T>;

  const stopStateWatch = watch(
    state,
    newVal => {
      if (!isUserScopeReady.value) {
        return;
      }

      setLocalStorageValue<T>(keyRef.value, newVal, options);
    },
    { deep: true }
  );

  const stopKeyWatch = watch(keyRef, () => {
    state.value = readCurrentScopedValue();
  });
  const stopUserIdWatch = watch(userIdRef, () => {
    state.value = readCurrentScopedValue();
  });

  function handleStorage(e: StorageEvent) {
    if (!isUserScopeReady.value) {
      return;
    }

    if (e.storageArea !== window.localStorage) {
      return;
    }

    if (e.key !== resolvedStorageKey.value) {
      return;
    }

    if (isNil(e.newValue)) {
      state.value = defaultValue;
      return;
    }

    try {
      const deserializer = options?.deserializer ?? defaultDeserialize<T>;
      state.value = deserializer(e.newValue);
    } catch {
      console.error('Error parsing localStorage value for key:', keyRef.value);
      // Should we set the fallback value as `defaultValue` here?
      // ignore parse errors
    }
  }

  if (isBrowser()) {
    window.addEventListener('storage', handleStorage);
  }

  const set = (value: T) => {
    state.value = value;
  };

  const get = (): T => {
    return state.value;
  };

  const dispose = () => {
    stopStateWatch();
    stopKeyWatch();
    stopUserIdWatch();
    if (isBrowser()) {
      window.removeEventListener('storage', handleStorage);
    }
  };

  // Pinia stores and other non-component callers have an effect scope but no component
  // instance; `onBeforeUnmount` only works in components (and breaks after async gaps in setup).
  const scope = getCurrentScope();
  if (scope) {
    onScopeDispose(dispose);
  } else if (getCurrentInstance()) {
    onBeforeUnmount(dispose);
  }

  return { ref: state, set, get, dispose };
}

export function useLocalStorage<T>(key: string, options?: StorageOptions<T>) {
  return {
    get: (fallback?: T) => getLocalStorageValue<T>(key, fallback, options),
    set: (value: T) => setLocalStorageValue<T>(key, value, options),
    remove: () => removeLocalStorageValue<T>(key, options)
  };
}
